import React, { useState } from 'react';
import { ContentType } from '../../api/bip-api/types';

interface TweetCreateFormProps {
  isGenerating: boolean;
  onGenerate: (contentType: ContentType) => void;
  onCancel: () => void;
}

const TweetCreateForm: React.FC<TweetCreateFormProps> = ({
  isGenerating,
  onGenerate,
  onCancel,
}) => {
  const [selectedType, setSelectedType] = useState<ContentType>(ContentType.FULL_REPORT);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onGenerate(selectedType);
  };

  return (
    <div className="h-full flex flex-col relative">
      {/* Loading Overlay */}
      {isGenerating && (
        <div className="absolute inset-0 bg-gray-900/90 flex flex-col items-center justify-center z-10 rounded-lg">
          {/* Wobbling Penguin */}
          <div className="text-8xl mb-6">
            <span className="inline-block animate-wiggle">
              🐧
            </span>
          </div>
          <p className="text-xl font-medium text-white mb-2">
            꽤 오래 걸리는 작업입니다!
          </p>
          <p className="text-sm text-gray-400">
            AI가 열심히 트윗을 작성하고 있어요...
          </p>
          <div className="mt-4 flex gap-1">
            <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></span>
            <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:0.2s]"></span>
            <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:0.4s]"></span>
          </div>
        </div>
      )}

      <h3 className="text-lg font-medium text-white mb-6">Generate New Tweet</h3>

      <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
        {/* Type Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Content Type
          </label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as ContentType)}
            className="w-full bg-gray-800 text-white rounded-lg px-4 py-2 border border-gray-600 focus:border-blue-500 focus:outline-none"
            disabled={isGenerating}
          >
            <option value={ContentType.FULL_REPORT}>Full Report</option>
            <option value={ContentType.SMART_MONEY}>Smart Money</option>
            <option value={ContentType.LORE}>Lore</option>
          </select>
        </div>

        {/* Type Description */}
        <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
          <p className="text-sm text-gray-400">
            {selectedType === ContentType.FULL_REPORT && (
              <>
                <span className="font-medium text-blue-400">Full Report:</span>{' '}
                토큰에 대한 종합적인 분석 리포트를 생성합니다.
              </>
            )}
            {selectedType === ContentType.SMART_MONEY && (
              <>
                <span className="font-medium text-green-400">Smart Money:</span>{' '}
                스마트머니/고래 활동 중심 트윗을 생성합니다.
              </>
            )}
            {selectedType === ContentType.LORE && (
              <>
                <span className="font-medium text-purple-400">Lore:</span>{' '}
                토큰의 배경 스토리와 내러티브 중심 트윗을 생성합니다.
              </>
            )}
          </p>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
            disabled={isGenerating}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Generating...
              </>
            ) : (
              'Generate'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TweetCreateForm;
